import React, { useEffect, useState, useRef, useCallback } from 'react';
import ProductCard from '../components/ProductCard';
import { ProductSkeleton } from './skeleton';
import { useGlobalShippingRates } from '../hooks/useGlobalShippingRates';
import API from '../api';

const PAGE_SIZE = 20;

// Utility functions
const parseSort = (sortValue) => {
  const sortMap = {
    price_asc: 'price_asc',
    price_desc: 'price_desc',
    title_asc: 'title_asc',
    title_desc: 'title_desc',
    date_asc: 'date_asc',
    date_desc: 'date_desc',
  };
  return { sort: sortMap[sortValue] || 'relevance' };
};

const buildApiParams = (filters, pageToLoad) => {
  const { sort } = parseSort(filters.sort);
  const params = {
    page: pageToLoad,
    size: PAGE_SIZE,
    sort,
    search: filters.search || '',
    category: filters.category || '',
  };

  // Add array filters - standardizziamo per usare 'vendor' come nel database
  if (filters.brands?.length > 0) {
    params.brands = filters.brands.join(',');
  }
  if (filters.vendors?.length > 0) {
    params.vendors = filters.vendors.join(',');
  }

  // Add price filter
  if (filters.price && (filters.price[0] > 0 || filters.price[1] > 0)) {
    params.price_min = filters.price[0];
    params.price_max = filters.price[1];
  }

  return params;
};

const getApiEndpoint = (mode, { categoryHandle, searchQuery, brand, sellerHandle }) => {
  const endpoints = {
    search: () => ({ url: '/search/products', params: { query: searchQuery } }),
    brand: () => ({ url: `/brands/${encodeURIComponent(brand)}/products` }),
    seller: () => ({ url: `/seller/${sellerHandle}/products` }),
    category: () => ({ url: `/categories/${categoryHandle}` }),
  };
  return endpoints[mode] || endpoints.category;
};

/**
 * ProductGrid universale per:
 * - mode='category': prodotti per categoria (richiede categoryHandle)
 * - mode='search': prodotti per ricerca (richiede searchQuery)
 * - mode='brand': prodotti per brand (richiede brand)
 * - mode='seller': prodotti per venditore (richiede sellerHandle)
 * - mode='custom': mostra solo i prodotti con gli id passati in productIds
 */
const ProductGrid = ({
  categoryHandle,
  filters,
  view,
  mode = 'category',
  searchQuery = '',
  brand = '',
  sellerHandle = '',
  productIds = null,
  loading: externalLoading = false,
  onResultsLoaded = null,
}) => {
  const [products, setProducts] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const observer = useRef();
  const loadingRef = useRef(false);
  const lastProductRef = useRef(null);
  const loadedProductIds = useRef(new Set()); // Tiene traccia degli ID già caricati

  const { getShippingRate, loadShippingRates } = useGlobalShippingRates();

  // Cleanup observer on unmount
  useEffect(() => {
    return () => {
      if (observer.current) {
        observer.current.disconnect();
        observer.current = null;
      }
    };
  }, []);

  // Custom mode: load specific products
  useEffect(() => {
    if (mode !== 'custom') return;

    const loadCustomProducts = async () => {
      if (!productIds?.length) {
        setProducts([]);
        setLoading(false);
        setError(null);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const res = await API.get('/products/bulk', { 
          params: { ids: productIds.join(',') } 
        });
        setProducts(res.data.products || []);
      } catch (err) {
        console.error('[ProductGrid] Errore bulkGetProducts:', err);
        setProducts([]);
        setError('Errore nel caricamento dei prodotti');
      } finally {
        setLoading(false);
      }
    };

    loadCustomProducts();
  }, [mode, productIds]);

  // Reset state when filters change (solo per modalità non-custom)
  useEffect(() => {
    if (mode === 'custom') return;
    
    console.log('[ProductGrid] Reset state per cambio filtri');
    
    // Disconnect observer before reset
    if (observer.current) {
      observer.current.disconnect();
      observer.current = null;
    }
    
    setProducts([]);
    setPage(1);
    setHasMore(true);
    setError(null);
    loadingRef.current = false;
    lastProductRef.current = null;
    loadedProductIds.current.clear(); // Reset del set degli ID caricati
  }, [categoryHandle, JSON.stringify(filters), searchQuery, mode, brand, sellerHandle]);

  // Fetch products paginated - SENZA useCallback per evitare dipendenze problematiche
  const fetchProducts = async (pageToLoad) => {
    if (loadingRef.current) {
      console.log('[ProductGrid] Fetch già in corso, skip');
      return;
    }
    
    console.log(`[ProductGrid] Fetching page ${pageToLoad}, mode: ${mode}`);
    
    loadingRef.current = true;
    setLoading(true);
    setError(null);
    
    try {
      const params = buildApiParams(filters, pageToLoad);
      const { url, params: additionalParams } = getApiEndpoint(mode, {
        categoryHandle,
        searchQuery,
        brand,
        sellerHandle
      })();

      const res = await API.get(url, {
        params: { ...params, ...additionalParams }
      });
      
      if (res.data.success) {
        const newProducts = res.data.products || [];
        console.log(`[ProductGrid] Ricevuti ${newProducts.length} prodotti per pagina ${pageToLoad}`);
        
        setProducts(prev => {
          if (pageToLoad === 1) {
            return newProducts;
          } else {
            return [...prev, ...newProducts];
          }
        });
        
        // Aggiorna hasMore basandosi sui risultati effettivi
        const hasMoreData = newProducts.length === PAGE_SIZE;
        console.log(`[ProductGrid] hasMore: ${hasMoreData} (${newProducts.length}/${PAGE_SIZE})`);
        setHasMore(hasMoreData);
        
        if (onResultsLoaded && pageToLoad === 1) {
          const totalResults = res.data.total || newProducts.length;
          onResultsLoaded(totalResults, newProducts);
        }
      } else {
        console.error('[ProductGrid] API non ha restituito success');
        setError('Errore nel caricamento dei prodotti');
        setHasMore(false);
      }
    } catch (err) {
      console.error('[ProductGrid] Errore fetchProducts:', err);
      setError('Errore nel caricamento dei prodotti');
      setHasMore(false);
    } finally {
      setLoading(false);
      loadingRef.current = false;
    }
  };

  // Load products on mount/filters change (solo per modalità non-custom)
  useEffect(() => {
    if (mode === 'custom') return;
    console.log('[ProductGrid] Caricamento iniziale prodotti');
    fetchProducts(1);
  }, [categoryHandle, JSON.stringify(filters), searchQuery, mode, brand, sellerHandle]);

  // Load shipping rates when products change - SOLO per nuovi prodotti
  useEffect(() => {
    if (products.length > 0) {
      // Identifica solo i nuovi prodotti e usa la prima variante
      const newVariantIds = products
        .map(product => {
          // Se il prodotto ha varianti, usa la prima, altrimenti usa il product ID
          return product.variants && product.variants.length > 0 
            ? (typeof product.variants[0] === 'string' ? product.variants[0] : product.variants[0].id)
            : null;
        })
        .filter(id => id && !loadedProductIds.current.has(id));
      
      if (newVariantIds.length > 0) {
        console.log(`[ProductGrid] Caricamento tariffe per ${newVariantIds.length} nuove varianti`);
        loadShippingRates(newVariantIds);
        
        // Aggiungi i nuovi ID al set
        newVariantIds.forEach(id => loadedProductIds.current.add(id));
      }
    }
  }, [products, loadShippingRates]);

  // Infinite scroll - setup semplificato
  const setupInfiniteScroll = useCallback(() => {
    if (loading || !hasMore || mode === 'custom') {
      console.log('[ProductGrid] Infinite scroll non attivo:', { loading, hasMore, mode });
      // Disconnect observer se non c'è più da caricare
      if (observer.current) {
        observer.current.disconnect();
        observer.current = null;
      }
      return;
    }

    // Disconnect previous observer
    if (observer.current) {
      observer.current.disconnect();
      observer.current = null;
    }

    // Create new observer
    observer.current = new window.IntersectionObserver(entries => {
      const entry = entries[0];
      console.log('[ProductGrid] Intersection observer triggered:', {
        isIntersecting: entry.isIntersecting,
        hasMore,
        loadingRef: loadingRef.current
      });
      
      if (entry.isIntersecting && hasMore && !loadingRef.current) {
        console.log('[ProductGrid] Loading next page...');
        setPage(prev => prev + 1);
      }
    }, {
      threshold: 0.1,
      rootMargin: '100px'
    });

    // Observe last product
    if (lastProductRef.current) {
      console.log('[ProductGrid] Observing last product');
      observer.current.observe(lastProductRef.current);
    }
  }, [loading, hasMore, mode]);

  // Setup infinite scroll when dependencies change
  useEffect(() => {
    setupInfiniteScroll();
  }, [setupInfiniteScroll]);

  // Disconnect observer when hasMore becomes false
  useEffect(() => {
    if (!hasMore && observer.current) {
      console.log('[ProductGrid] Disconnecting observer - no more pages');
      observer.current.disconnect();
      observer.current = null;
    }
  }, [hasMore]);

  // Fetch next page
  useEffect(() => {
    if (mode === 'custom' || page === 1) return;
    console.log(`[ProductGrid] Fetching page ${page}`);
    fetchProducts(page);
  }, [page, mode]);

  const isLoading = externalLoading || loading;

  return (
    <div className="product-grid-container">
      <div className={`product-grid ${view === 'list' ? 'product-grid-list' : 'product-grid-grid'}`}>
        {products.map((product, index) => {
          const isLastProduct = mode !== 'custom' && index === products.length - 1;
          
          return (
            <div
              className={`product-grid-item ${view === 'grid' ? 'product-grid-item-grid' : 'product-grid-item-list'}`}
              key={product.id}
              data-infinite-scroll={isLastProduct ? 'true' : 'false'}
              ref={isLastProduct ? (node) => {
                lastProductRef.current = node;
                // Setup observer after ref is set
                if (node && observer.current) {
                  observer.current.observe(node);
                }
              } : null}
            >
              <ProductCard 
                product={product} 
                view={view} 
                shippingRate={getShippingRate(product.variants && product.variants.length > 0 ? (typeof product.variants[0] === 'string' ? product.variants[0] : product.variants[0].id) : undefined)}
                shippingLoading={false} // puoi migliorare se vuoi gestire loading per prodotto
              />
            </div>
          );
        })}
      </div>
      {isLoading && <ProductSkeleton count={8} />}
      {error && <div className="product-grid-error">{error}</div>}
      {!isLoading && !error && products.length === 0 && (
        <div className="product-grid-empty">Nessun prodotto trovato.</div>
      )}
      {!isLoading && !error && !hasMore && products.length > 0 && (
        <div className="product-grid-end">Hai raggiunto la fine dei risultati</div>
      )}
    </div>
  );
};

export default ProductGrid;
